
import React, { useState, useEffect, useRef } from 'react';
import { Bold, Italic, ListOrdered, List } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = "Enter description...",
  className
}) => {
  const [isEmpty, setIsEmpty] = useState(!value);
  const editorRef = useRef<HTMLDivElement>(null);
  const selectionRef = useRef<{
    start: number;
    end: number;
    container: Node | null;
  }>({
    start: 0,
    end: 0,
    container: null
  });

  // Initialize editor on mount
  useEffect(() => {
    if (editorRef.current) {
      // Set initial content and direction
      editorRef.current.innerHTML = value;
      editorRef.current.dir = "ltr";
      editorRef.current.style.textAlign = "left";
      
      // Set initial isEmpty state
      setIsEmpty(!value || value === '' || value === '<br>');
    }
  }, []);

  // Update content when value prop changes from outside
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      // Save current selection before updating content
      saveSelection();
      
      // Update content
      editorRef.current.innerHTML = value;
      
      // Restore direction settings
      editorRef.current.dir = "ltr";
      editorRef.current.style.textAlign = "left";
      
      // Restore selection after updating content
      restoreSelection();
      
      // Update isEmpty state
      setIsEmpty(!value || value === '' || value === '<br>');
    }
  }, [value]);

  // Save the current selection position
  const saveSelection = () => {
    if (!editorRef.current || !window.getSelection) return;
    
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    
    const range = selection.getRangeAt(0);
    
    // Only save selection if it's within our editor
    if (!editorRef.current.contains(range.commonAncestorContainer)) return;
    
    selectionRef.current = {
      start: range.startOffset,
      end: range.endOffset,
      container: range.startContainer
    };
  };

  // Restore the previously saved selection
  const restoreSelection = () => {
    if (!editorRef.current || !window.getSelection || !selectionRef.current.container) return;
    
    try {
      const selection = window.getSelection();
      if (!selection) return;
      
      // Only restore if the container is still in the DOM
      if (editorRef.current.contains(selectionRef.current.container)) {
        const range = document.createRange();
        range.setStart(selectionRef.current.container, selectionRef.current.start);
        range.setEnd(selectionRef.current.container, selectionRef.current.end);
        
        selection.removeAllRanges();
        selection.addRange(range);
      } else {
        // If the container is no longer in the DOM, set cursor at the end
        setEndOfContentEditable(editorRef.current);
      }
    } catch (error) {
      console.error("Error restoring selection:", error);
      // Fall back to setting cursor at the end
      setEndOfContentEditable(editorRef.current);
    }
  };

  // Helper function to set cursor at the end of content editable
  const setEndOfContentEditable = (contentEditableElement: HTMLElement) => {
    if (!contentEditableElement || !window.getSelection) return;
    
    const selection = window.getSelection();
    if (!selection) return;
    
    const range = document.createRange();
    range.selectNodeContents(contentEditableElement);
    range.collapse(false); // collapse to end
    
    selection.removeAllRanges();
    selection.addRange(range);
    contentEditableElement.focus();
  };

  // Handle content changes
  const handleChange = (e: React.FormEvent<HTMLDivElement>) => {
    if (!editorRef.current) return;
    
    // Save current selection before we do anything
    saveSelection();
    
    // Get content and notify parent
    const content = editorRef.current.innerHTML;
    onChange(content);
    
    // Update isEmpty state
    setIsEmpty(!content || content === '<br>' || content === '');
  };

  // Execute formatting command
  const formatText = (command: string, value: string = '') => {
    if (!editorRef.current) return;
    
    // Focus the editor before executing command
    editorRef.current.focus();
    
    // Execute the command
    document.execCommand(command, false, value);
    
    // Update content after formatting
    const content = editorRef.current.innerHTML;
    onChange(content);
    setIsEmpty(!content || content === '<br>' || content === '');
    
    // Ensure editor maintains focus after command
    editorRef.current.focus();
  };

  // Clear all content
  const clearFormat = () => {
    if (!editorRef.current) return;
    
    // Clear content
    editorRef.current.innerHTML = '';
    
    // Notify parent of change
    onChange('');
    setIsEmpty(true);
    
    // Focus the editor
    editorRef.current.focus();
  };

  return (
    <div className={cn("space-y-2 w-full", className)}>
      <div className="flex items-center justify-between bg-card border border-border rounded-t-md p-2">
        <div className="flex items-center space-x-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => formatText('bold')}
            type="button"
            className="h-8 w-8"
            onMouseDown={(e) => e.preventDefault()} // Prevent losing focus
          >
            <Bold className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => formatText('italic')}
            type="button"
            className="h-8 w-8"
            onMouseDown={(e) => e.preventDefault()} // Prevent losing focus
          >
            <Italic className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => formatText('insertOrderedList')}
            type="button"
            className="h-8 w-8"
            onMouseDown={(e) => e.preventDefault()} // Prevent losing focus
          >
            <ListOrdered className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => formatText('insertUnorderedList')}
            type="button"
            className="h-8 w-8"
            onMouseDown={(e) => e.preventDefault()} // Prevent losing focus
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={clearFormat}
          type="button"
          className="text-xs h-8"
          onMouseDown={(e) => e.preventDefault()} // Prevent losing focus
        >
          Clear draft
        </Button>
      </div>
      
      <div className="relative">
        <div
          ref={editorRef}
          contentEditable
          dir="ltr"
          style={{ textAlign: 'left' }}
          className="min-h-[300px] p-4 bg-card border border-border rounded-b-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary overflow-auto"
          onInput={handleChange}
          onBlur={saveSelection}
          onFocus={() => {
            // Ensure LTR direction is maintained when focusing
            if (editorRef.current) {
              editorRef.current.dir = "ltr";
              editorRef.current.style.textAlign = "left";
            }
          }}
          onClick={() => {
            // Save selection when clicked
            saveSelection();
          }}
        />
        {isEmpty && placeholder && (
          <div className="absolute top-0 left-0 p-4 pointer-events-none text-muted-foreground opacity-60 text-left">
            {placeholder}
          </div>
        )}
      </div>
    </div>
  );
};
